import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, User, CreditCard, MapPin, Building2, Check, ChevronRight, 
  AlertCircle, Shield, Loader2, Phone
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Delhi", "Jammu and Kashmir", "Ladakh"
];

const STEPS = [
  { id: 1, title: 'Personal Details', icon: User },
  { id: 2, title: 'Address', icon: MapPin },
  { id: 3, title: 'Bank Details', icon: Building2 },
  { id: 4, title: 'Review & Submit', icon: Check }
];

export default function KYCModal({ isOpen, onClose, userId, onComplete }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  
  // Form data
  const [personalDetails, setPersonalDetails] = useState({
    fullName: '',
    pan: '',
    aadhar: '',
    dob: ''
  });
  
  const [address, setAddress] = useState({
    line1: '',
    line2: '',
    city: '',
    state: '',
    pincode: ''
  });
  
  const [bankDetails, setBankDetails] = useState({
    accountHolderName: '',
    accountNumber: '',
    confirmAccountNumber: '',
    ifsc: '',
    bankName: ''
  });
  
  // Verification states
  const [panVerified, setPanVerified] = useState(false);
  const [panVerifiedName, setPanVerifiedName] = useState('');
  const [digilockerInitiated, setDigilockerInitiated] = useState(false);
  const [digilockerTxnId, setDigilockerTxnId] = useState('');
  const [digilockerAuthUrl, setDigilockerAuthUrl] = useState('');
  const [aadharVerified, setAadharVerified] = useState(false);
  const [aadharVerifiedData, setAadharVerifiedData] = useState(null);
  const [bankVerified, setBankVerified] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Storage key for persisting KYC progress
  const STORAGE_KEY = `kyc_progress_${userId}`;

  // Load saved progress on mount and when modal opens
  useEffect(() => {
    if (isOpen && userId) {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        try {
          const data = JSON.parse(saved);
          // Restore form data
          if (data.personalDetails) setPersonalDetails(data.personalDetails);
          if (data.address) setAddress(data.address);
          if (data.bankDetails) setBankDetails(data.bankDetails);
          // Restore verification states
          if (data.panVerified) setPanVerified(true);
          if (data.panVerifiedName) setPanVerifiedName(data.panVerifiedName);
          if (data.aadharVerified) setAadharVerified(true);
          if (data.aadharVerifiedData) setAadharVerifiedData(data.aadharVerifiedData);
          if (data.bankVerified) setBankVerified(true);
          if (data.currentStep) setCurrentStep(data.currentStep);
          // DigiLocker state
          if (data.digilockerTxnId) {
            setDigilockerTxnId(data.digilockerTxnId);
            setDigilockerInitiated(true);
            if (data.digilockerAuthUrl) setDigilockerAuthUrl(data.digilockerAuthUrl);
          }
          console.log('[KYC] Restored progress from storage');
        } catch (e) {
          console.error('[KYC] Failed to parse saved progress:', e);
        }
      }
    }
  }, [isOpen, userId, STORAGE_KEY]);

  // Save progress to localStorage whenever important state changes
  useEffect(() => {
    if (userId && (panVerified || aadharVerified || personalDetails.pan || digilockerTxnId)) {
      const dataToSave = {
        personalDetails,
        address,
        bankDetails,
        panVerified,
        panVerifiedName,
        aadharVerified,
        aadharVerifiedData,
        bankVerified,
        currentStep,
        digilockerTxnId,
        digilockerAuthUrl,
        savedAt: new Date().toISOString()
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
    }
  }, [userId, personalDetails, address, bankDetails, panVerified, panVerifiedName, 
      aadharVerified, aadharVerifiedData, bankVerified, currentStep, 
      digilockerTxnId, digilockerAuthUrl, STORAGE_KEY]);

  // Clear progress only on successful KYC submission (not on close)
  const clearSavedProgress = () => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem('digilocker_txn_id');
    localStorage.removeItem('digilocker_user_id');
  };

  // Reset only transient state on close, NOT verification states
  useEffect(() => {
    if (!isOpen) {
      // Only reset step to 1, keep verification data
      setCurrentStep(1);
      setVerifying(false);
      setLoading(false);
    }
  }, [isOpen]);

  // Verify PAN
  const handleVerifyPAN = async () => {
    if (!personalDetails.pan || !personalDetails.fullName) {
      toast.error('Please enter PAN and Full Name first');
      return;
    }
    
    setVerifying(true);
    try {
      const response = await fetch(
        `${API_URL}/api/investor/kyc/verify-pan?user_id=${userId}&pan=${personalDetails.pan}&name=${encodeURIComponent(personalDetails.fullName)}&dob=${personalDetails.dob}`,
        { method: 'POST' }
      );
      const data = await response.json();
      
      if (data.success) {
        setPanVerified(true);
        setPanVerifiedName(data.registeredName);
        toast.success('PAN verified successfully!');
      } else {
        toast.error(data.error || 'PAN verification failed');
      }
    } catch (error) {
      toast.error('Failed to verify PAN');
    } finally {
      setVerifying(false);
    }
  };

  // Send Aadhar OTP - REPLACED WITH DIGILOCKER
  const handleInitiateDigilocker = async () => {
    if (!personalDetails.aadhar) {
      toast.error('Please enter Aadhar number first');
      return;
    }
    
    setVerifying(true);
    try {
      // Get the current page URL for redirect callback
      const callbackUrl = `${window.location.origin}/investor/kyc/callback`;
      
      const response = await fetch(`${API_URL}/api/investor/kyc/digilocker/initiate?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ redirect_url: callbackUrl })
      });
      const data = await response.json();
      
      if (data.success && data.authorizationUrl) {
        setDigilockerInitiated(true);
        setDigilockerTxnId(data.decentroTxnId);
        setDigilockerAuthUrl(data.authorizationUrl);
        
        // Store in localStorage for persistence across redirect
        localStorage.setItem('digilocker_txn_id', data.decentroTxnId);
        localStorage.setItem('digilocker_user_id', userId);
        
        // Open DigiLocker in new tab
        window.open(data.authorizationUrl, '_blank');
        
        toast.success('DigiLocker opened in new tab. Complete verification there and return here.');
      } else {
        toast.error(data.error || 'Failed to initiate DigiLocker');
      }
    } catch (error) {
      toast.error('Failed to initiate DigiLocker');
    } finally {
      setVerifying(false);
    }
  };

  // Complete DigiLocker verification after user returns
  const handleCompleteDigilocker = async () => {
    const txnId = digilockerTxnId || localStorage.getItem('digilocker_txn_id');
    
    if (!txnId) {
      toast.error('Please initiate DigiLocker verification first');
      return;
    }
    
    setVerifying(true);
    try {
      const response = await fetch(`${API_URL}/api/investor/kyc/digilocker/verify?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decentro_txn_id: txnId })
      });
      const data = await response.json();
      
      if (data.success && data.verified) {
        setAadharVerified(true);
        setAadharVerifiedData(data.aadhaarData);
        
        // Auto-fill address from DigiLocker data
        if (data.aadhaarData?.address) {
          const addr = data.aadhaarData.address;
          setAddress(prev => ({
            ...prev,
            line1: [addr.house, addr.street].filter(Boolean).join(', ') || prev.line1,
            line2: [addr.locality, addr.landmark].filter(Boolean).join(', ') || prev.line2,
            city: addr.district || addr.vtc || prev.city,
            state: addr.state || prev.state,
            pincode: String(addr.pincode || '') || prev.pincode
          }));
        }
        
        // Auto-fill name if not set
        if (data.aadhaarData?.name && !personalDetails.fullName) {
          setPersonalDetails(prev => ({ ...prev, fullName: data.aadhaarData.name }));
        }
        
        // Clear localStorage
        localStorage.removeItem('digilocker_txn_id');
        localStorage.removeItem('digilocker_user_id');
        
        toast.success('Aadhaar verified successfully via DigiLocker!');
      } else {
        toast.error(data.error || 'DigiLocker verification failed. Please complete verification on DigiLocker first.');
      }
    } catch (error) {
      toast.error('Failed to verify DigiLocker');
    } finally {
      setVerifying(false);
    }
  };

  // Check for pending DigiLocker verification on mount
  useEffect(() => {
    const storedTxnId = localStorage.getItem('digilocker_txn_id');
    const storedUserId = localStorage.getItem('digilocker_user_id');
    
    if (storedTxnId && storedUserId === userId) {
      setDigilockerInitiated(true);
      setDigilockerTxnId(storedTxnId);
    }
  }, [userId]);

  // Verify Bank Account
  const handleVerifyBank = async () => {
    if (!bankDetails.accountNumber || !bankDetails.ifsc) {
      toast.error('Please enter account number and IFSC');
      return;
    }
    
    setVerifying(true);
    try {
      const response = await fetch(
        `${API_URL}/api/investor/kyc/verify-bank?user_id=${userId}&account_number=${bankDetails.accountNumber}&ifsc=${bankDetails.ifsc}&account_holder_name=${encodeURIComponent(bankDetails.accountHolderName)}`,
        { method: 'POST' }
      );
      const data = await response.json();
      
      if (data.success) {
        setBankVerified(true);
        if (data.bankName) {
          setBankDetails(prev => ({ ...prev, bankName: data.bankName }));
        }
        toast.success('Bank account verified!');
      } else {
        toast.error(data.error || 'Bank verification failed');
      }
    } catch (error) {
      toast.error('Failed to verify bank account');
    } finally {
      setVerifying(false);
    }
  };

  // Submit KYC
  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/investor/kyc/submit?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          personalDetails,
          address,
          bankDetails: {
            accountHolderName: bankDetails.accountHolderName,
            accountNumber: bankDetails.accountNumber,
            ifsc: bankDetails.ifsc,
            bankName: bankDetails.bankName
          }
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        clearSavedProgress(); // Clear localStorage on successful submission
        toast.success('KYC submitted successfully!');
        onComplete?.();
        onClose();
      } else {
        toast.error(data.detail || 'Failed to submit KYC');
      }
    } catch (error) {
      toast.error('Failed to submit KYC');
    } finally {
      setLoading(false);
    }
  };

  const canProceedStep1 = personalDetails.fullName && personalDetails.pan && personalDetails.aadhar && personalDetails.dob && panVerified;
  const canProceedStep2 = address.line1 && address.city && address.state && address.pincode;
  const canProceedStep3 = bankDetails.accountHolderName && bankDetails.accountNumber && bankDetails.ifsc && 
                          bankDetails.accountNumber === bankDetails.confirmAccountNumber;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="w-full max-w-2xl bg-[#0a0a0a] border border-white/10 rounded-2xl overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <div>
              <h2 className="text-xl font-medium text-white" data-testid="kyc-modal-title">Complete Your KYC</h2>
              <p className="text-sm text-white/60 mt-1">Required to start investing</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 bg-white/5">
            {STEPS.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className={`flex items-center gap-2 ${currentStep >= step.id ? 'text-[#4ADE80]' : 'text-white/40'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                    currentStep > step.id ? 'bg-[#4ADE80] border-[#4ADE80]' :
                    currentStep === step.id ? 'border-[#4ADE80]' : 'border-white/20'
                  }`}>
                    {currentStep > step.id ? (
                      <Check className="w-4 h-4 text-black" />
                    ) : (
                      <step.icon className="w-4 h-4" />
                    )}
                  </div>
                  <span className="text-sm hidden sm:block">{step.title}</span>
                </div>
                {index < STEPS.length - 1 && (
                  <ChevronRight className="w-4 h-4 mx-2 text-white/20" />
                )}
              </div>
            ))}
          </div>

          {/* Form Content */}
          <div className="p-6 max-h-[60vh] overflow-y-auto">
            {/* Step 1: Personal Details */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/80">Full Name (as per PAN) *</Label>
                    <Input
                      value={personalDetails.fullName}
                      onChange={e => setPersonalDetails(prev => ({ ...prev, fullName: e.target.value }))}
                      className="mt-1 bg-white/5 border-white/10 text-white"
                      placeholder="RAHUL SHARMA"
                      data-testid="kyc-fullname"
                    />
                  </div>
                  <div>
                    <Label className="text-white/80">Date of Birth *</Label>
                    <Input
                      type="date"
                      value={personalDetails.dob}
                      onChange={e => setPersonalDetails(prev => ({ ...prev, dob: e.target.value }))}
                      className="mt-1 bg-white/5 border-white/10 text-white"
                      data-testid="kyc-dob"
                    />
                  </div>
                </div>

                {/* PAN Verification */}
                <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                  <div className="flex items-center gap-2 mb-3">
                    <CreditCard className="w-4 h-4 text-white/60" />
                    <span className="text-sm font-medium text-white">PAN Verification</span>
                    {panVerified && <Check className="w-4 h-4 text-[#4ADE80]" />}
                  </div>
                  <div className="flex gap-3">
                    <Input
                      value={personalDetails.pan}
                      onChange={e => setPersonalDetails(prev => ({ ...prev, pan: e.target.value.toUpperCase() }))}
                      className="flex-1 bg-white/5 border-white/10 text-white uppercase"
                      placeholder="ABCDE1234F"
                      maxLength={10}
                      disabled={panVerified}
                      data-testid="kyc-pan"
                    />
                    <Button
                      onClick={handleVerifyPAN}
                      disabled={verifying || panVerified || !personalDetails.pan || !personalDetails.fullName}
                      className={panVerified ? 'bg-[#4ADE80] text-black' : 'bg-white text-black hover:bg-gray-200'}
                    >
                      {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : panVerified ? 'Verified' : 'Verify'}
                    </Button>
                  </div>
                  {panVerifiedName && (
                    <p className="text-xs text-[#4ADE80] mt-2">✓ Registered as: {panVerifiedName}</p>
                  )}
                </div>

                {/* Aadhar Verification via DigiLocker */}
                <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                  <div className="flex items-center gap-2 mb-3">
                    <Shield className="w-4 h-4 text-white/60" />
                    <span className="text-sm font-medium text-white">Aadhaar Verification (DigiLocker)</span>
                    {aadharVerified && <Check className="w-4 h-4 text-[#4ADE80]" />}
                  </div>
                  
                  {!aadharVerified && (
                    <>
                      <div className="flex gap-3 mb-3">
                        <Input
                          value={personalDetails.aadhar}
                          onChange={e => setPersonalDetails(prev => ({ ...prev, aadhar: e.target.value.replace(/\D/g, '') }))}
                          className="flex-1 bg-white/5 border-white/10 text-white"
                          placeholder="1234 5678 9012"
                          maxLength={12}
                          disabled={aadharVerified}
                          data-testid="kyc-aadhar"
                        />
                      </div>
                      
                      {!digilockerInitiated ? (
                        <Button
                          onClick={handleInitiateDigilocker}
                          disabled={verifying || !personalDetails.aadhar || personalDetails.aadhar.length !== 12}
                          className="w-full bg-blue-600 text-white hover:bg-blue-700"
                          data-testid="kyc-digilocker-btn"
                        >
                          {verifying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
                          Verify with DigiLocker
                        </Button>
                      ) : (
                        <div className="space-y-3">
                          <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                            <p className="text-sm text-blue-200">
                              DigiLocker opened in a new tab. Complete the verification there, then click the button below.
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              onClick={() => window.open(digilockerAuthUrl, '_blank')}
                              variant="outline"
                              className="flex-1 border-white/20 text-white hover:bg-white/10"
                            >
                              Open DigiLocker Again
                            </Button>
                            <Button
                              onClick={handleCompleteDigilocker}
                              disabled={verifying}
                              className="flex-1 bg-[#4ADE80] text-black hover:bg-[#4ADE80]/90"
                              data-testid="kyc-digilocker-complete-btn"
                            >
                              {verifying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                              I've Completed Verification
                            </Button>
                          </div>
                          <Button
                            onClick={() => {
                              setDigilockerInitiated(false);
                              setDigilockerTxnId('');
                              setDigilockerAuthUrl('');
                              localStorage.removeItem('digilocker_txn_id');
                              localStorage.removeItem('digilocker_user_id');
                            }}
                            variant="ghost"
                            className="w-full text-white/50 hover:text-white hover:bg-white/5 text-xs"
                          >
                            Start Over (if session expired)
                          </Button>
                        </div>
                      )}
                      
                      <p className="text-xs text-white/40 mt-3">
                        You'll be redirected to DigiLocker to securely verify your Aadhaar. No OTP required.
                      </p>
                    </>
                  )}
                  
                  {aadharVerified && aadharVerifiedData && (
                    <div className="space-y-2">
                      <p className="text-xs text-[#4ADE80]">✓ Aadhaar verified via DigiLocker</p>
                      <div className="text-xs text-white/60 space-y-1">
                        <p>Name: <span className="text-white">{aadharVerifiedData.name}</span></p>
                        <p>UID: <span className="text-white">{aadharVerifiedData.uid}</span></p>
                        {aadharVerifiedData.dob && <p>DOB: <span className="text-white">{aadharVerifiedData.dob}</span></p>}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 2: Address */}
            {currentStep === 2 && (
              <div className="space-y-4">
                <div>
                  <Label className="text-white/80">Address Line 1 *</Label>
                  <Input
                    value={address.line1}
                    onChange={e => setAddress(prev => ({ ...prev, line1: e.target.value }))}
                    className="mt-1 bg-white/5 border-white/10 text-white"
                    placeholder="Flat/House No, Building Name"
                    data-testid="kyc-address1"
                  />
                </div>
                <div>
                  <Label className="text-white/80">Address Line 2</Label>
                  <Input
                    value={address.line2}
                    onChange={e => setAddress(prev => ({ ...prev, line2: e.target.value }))}
                    className="mt-1 bg-white/5 border-white/10 text-white"
                    placeholder="Street, Area, Landmark"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/80">City *</Label>
                    <Input
                      value={address.city}
                      onChange={e => setAddress(prev => ({ ...prev, city: e.target.value }))}
                      className="mt-1 bg-white/5 border-white/10 text-white"
                      placeholder="Mumbai"
                      data-testid="kyc-city"
                    />
                  </div>
                  <div>
                    <Label className="text-white/80">Pincode *</Label>
                    <Input
                      value={address.pincode}
                      onChange={e => setAddress(prev => ({ ...prev, pincode: e.target.value.replace(/\D/g, '') }))}
                      className="mt-1 bg-white/5 border-white/10 text-white"
                      placeholder="400001"
                      maxLength={6}
                      data-testid="kyc-pincode"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-white/80">State *</Label>
                  <select
                    value={address.state}
                    onChange={e => setAddress(prev => ({ ...prev, state: e.target.value }))}
                    className="w-full mt-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"
                    data-testid="kyc-state"
                  >
                    <option value="" className="bg-[#0a0a0a]">Select State</option>
                    {INDIAN_STATES.map(state => (
                      <option key={state} value={state} className="bg-[#0a0a0a]">{state}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Step 3: Bank Details */}
            {currentStep === 3 && (
              <div className="space-y-4">
                <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-yellow-200">
                      Bank account must be in the same name as your PAN. This account will be used for all payouts.
                    </p>
                  </div>
                </div>

                <div>
                  <Label className="text-white/80">Account Holder Name *</Label>
                  <Input
                    value={bankDetails.accountHolderName}
                    onChange={e => setBankDetails(prev => ({ ...prev, accountHolderName: e.target.value }))}
                    className="mt-1 bg-white/5 border-white/10 text-white"
                    placeholder="RAHUL SHARMA"
                    data-testid="kyc-bank-name"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/80">Account Number *</Label>
                    <Input
                      value={bankDetails.accountNumber}
                      onChange={e => setBankDetails(prev => ({ ...prev, accountNumber: e.target.value.replace(/\D/g, '') }))}
                      className="mt-1 bg-white/5 border-white/10 text-white"
                      placeholder="50100123456789"
                      data-testid="kyc-bank-account"
                    />
                  </div>
                  <div>
                    <Label className="text-white/80">Confirm Account Number *</Label>
                    <Input
                      value={bankDetails.confirmAccountNumber}
                      onChange={e => setBankDetails(prev => ({ ...prev, confirmAccountNumber: e.target.value.replace(/\D/g, '') }))}
                      className="mt-1 bg-white/5 border-white/10 text-white"
                      placeholder="50100123456789"
                    />
                    {bankDetails.confirmAccountNumber && bankDetails.accountNumber !== bankDetails.confirmAccountNumber && (
                      <p className="text-xs text-red-400 mt-1">Account numbers don't match</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/80">IFSC Code *</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        value={bankDetails.ifsc}
                        onChange={e => setBankDetails(prev => ({ ...prev, ifsc: e.target.value.toUpperCase() }))}
                        className="flex-1 bg-white/5 border-white/10 text-white uppercase"
                        placeholder="HDFC0001234"
                        maxLength={11}
                        data-testid="kyc-bank-ifsc"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-white/80">Bank Name</Label>
                    <Input
                      value={bankDetails.bankName}
                      onChange={e => setBankDetails(prev => ({ ...prev, bankName: e.target.value }))}
                      className="mt-1 bg-white/5 border-white/10 text-white"
                      placeholder="HDFC Bank"
                    />
                  </div>
                </div>

                <Button
                  onClick={handleVerifyBank}
                  disabled={verifying || bankVerified || !bankDetails.accountNumber || !bankDetails.ifsc}
                  className={`w-full ${bankVerified ? 'bg-[#4ADE80] text-black' : 'bg-white/10 text-white hover:bg-white/20'}`}
                >
                  {verifying ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : bankVerified ? (
                    <Check className="w-4 h-4 mr-2" />
                  ) : null}
                  {bankVerified ? 'Bank Account Verified' : 'Verify Bank Account (Optional)'}
                </Button>
              </div>
            )}

            {/* Step 4: Review */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                  <h3 className="text-sm font-medium text-white mb-3">Personal Details</h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-white/60">Name:</span>
                      <span className="text-white ml-2">{personalDetails.fullName}</span>
                    </div>
                    <div>
                      <span className="text-white/60">DOB:</span>
                      <span className="text-white ml-2">{personalDetails.dob}</span>
                    </div>
                    <div>
                      <span className="text-white/60">PAN:</span>
                      <span className="text-white ml-2">{personalDetails.pan.substring(0, 4)}****{personalDetails.pan.slice(-2)}</span>
                      {panVerified && <Check className="w-3 h-3 text-[#4ADE80] inline ml-1" />}
                    </div>
                    <div>
                      <span className="text-white/60">Aadhar:</span>
                      <span className="text-white ml-2">XXXX-XXXX-{personalDetails.aadhar.slice(-4)}</span>
                      {aadharVerified && (
                        <span className="inline-flex items-center ml-1">
                          <Check className="w-3 h-3 text-[#4ADE80]" />
                          <span className="text-xs text-[#4ADE80] ml-1">(DigiLocker)</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                  <h3 className="text-sm font-medium text-white mb-3">Address</h3>
                  <p className="text-sm text-white/80">
                    {address.line1}{address.line2 && `, ${address.line2}`}<br />
                    {address.city}, {address.state} - {address.pincode}
                  </p>
                </div>

                <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                  <h3 className="text-sm font-medium text-white mb-3">Bank Details</h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-white/60">Name:</span>
                      <span className="text-white ml-2">{bankDetails.accountHolderName}</span>
                    </div>
                    <div>
                      <span className="text-white/60">Account:</span>
                      <span className="text-white ml-2">****{bankDetails.accountNumber.slice(-4)}</span>
                      {bankVerified && <Check className="w-3 h-3 text-[#4ADE80] inline ml-1" />}
                    </div>
                    <div>
                      <span className="text-white/60">IFSC:</span>
                      <span className="text-white ml-2">{bankDetails.ifsc}</span>
                    </div>
                    <div>
                      <span className="text-white/60">Bank:</span>
                      <span className="text-white ml-2">{bankDetails.bankName || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-[#4ADE80]/10 border border-[#4ADE80]/30 rounded-xl">
                  <div className="flex items-start gap-2">
                    <Shield className="w-5 h-5 text-[#4ADE80] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-[#4ADE80] font-medium">Secure & Compliant</p>
                      <p className="text-xs text-white/60 mt-1">
                        Your data is encrypted and stored securely. KYC verification is mandatory as per SEBI regulations.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-white/10 bg-white/5">
            <Button
              variant="ghost"
              onClick={() => currentStep > 1 ? setCurrentStep(currentStep - 1) : onClose()}
              className="text-white/60 hover:text-white"
            >
              {currentStep > 1 ? 'Back' : 'Cancel'}
            </Button>
            
            {currentStep < 4 ? (
              <Button
                onClick={() => setCurrentStep(currentStep + 1)}
                disabled={
                  (currentStep === 1 && !canProceedStep1) ||
                  (currentStep === 2 && !canProceedStep2) ||
                  (currentStep === 3 && !canProceedStep3)
                }
                className="bg-white text-black hover:bg-gray-200"
                data-testid="kyc-next-btn"
              >
                Continue <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="bg-[#4ADE80] text-black hover:bg-[#4ADE80]/90"
                data-testid="kyc-submit-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Submit KYC
              </Button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
